import React, { ReactElement } from 'react';
import { Route, Switch, useLocation } from 'react-router-dom';
import useNav from 'hooks/nav';
import backIcon from '../../assets/svgs/icon-chevron-left.svg';
import { useTranslate } from 'hooks/translate';

import Identification from 'scenes/booking/identification/identification';
import ContactDetails from 'scenes/booking/contact-details/contact-details';
import ParkingSlots from 'scenes/booking/parking-slots/parking-slots';
import BookingSummary from 'scenes/booking/booking-summary/booking-summary';

import './booking-layout.scss';

import {
  BOOKING_IDENTIFICATION_PATH,
  BOOKING_INFORMATION_PATH,
  BOOKING_PARKING_PATH,
  BOOKING_PAYMENT_PROCESSING_PATH,
  BOOKING_SUMMARY_PATH,
  LOGIN_PATH,
  PARKS_PATH,
} from 'constants/paths';
import { useReservations } from 'contexts/reservations-context';
import { BOOKING_PAGES } from 'constants/booking';
import BookingPaymentProcessing from 'scenes/booking/booking-payment-proccessing/booking-payment-processing';

export default function BookingLayout(): ReactElement {
  const { goTo } = useNav();
  const location = useLocation();
  const { t } = useTranslate();
  const { page, parkId, setPage, parkToReserve } = useReservations();
  const styleOverride = parkToReserve?.hostUrl ? { padding: '10px 50px' } : {};
  const onBackButtonClick = (event: any): void => {
    event.preventDefault();

    switch (page) {
      case BOOKING_PAGES.SLOT_PICKER:
        goTo(`${PARKS_PATH}/${parkId}`);
        break;
      case BOOKING_PAGES.PASSENGER_INFO:
        goTo(BOOKING_INFORMATION_PATH);
        setPage(BOOKING_PAGES.CONTACT_DETAILS);
        break;
      case BOOKING_PAGES.BOOKING_SUMMARY:
        if (parkToReserve?.requirePassengerDeclaration) {
          setPage(BOOKING_PAGES.PASSENGER_INFO);
        } else {
          setPage(BOOKING_PAGES.CONTACT_DETAILS);
        }
        break;
      case BOOKING_PAGES.CONTACT_DETAILS:
        goTo(BOOKING_PARKING_PATH);
        setPage(BOOKING_PAGES.SLOT_PICKER);
        break;
    }
  };

  const getSteps = (): string[] => {
    const stepList = [
      BOOKING_PARKING_PATH,
      BOOKING_IDENTIFICATION_PATH,
      BOOKING_SUMMARY_PATH,
    ];

    if (parkToReserve.requirePassengerDeclaration) {
      stepList.splice(1, 0, BOOKING_INFORMATION_PATH);
    }

    return stepList;
  };

  const steps = getSteps();

  return (
    <div className='booking-layout'>
      <div className='booking-navigation' style={styleOverride}>
        <div className='booking-action'>
          <button onClick={onBackButtonClick}>
            <img src={backIcon} alt='Go Back' />
          </button>
        </div>

        <div className='booking-title'>
          {!location.pathname.includes(LOGIN_PATH) ? (
            <p>
              {t('reservation.registration.booking')} –{' '}
              {!parkToReserve.requirePassengerDeclaration && page === 4 // eslint-disable-line @typescript-eslint/prefer-nullish-coalescing
                ? page - 1
                : page}{' '}
              {t('reservation.registration.of')} {steps.length}
            </p>
          ) : (
            <p>Sign In</p>
          )}
        </div>
        <div className='booking-action' />
      </div>
      <div className='layout-content' style={styleOverride}>
        <Switch>
          <Route
            path={`/:locale?${LOGIN_PATH}`}
            render={() => <Identification back={onBackButtonClick} />}
          />
          <Route
            path={`/:locale?${BOOKING_IDENTIFICATION_PATH}`}
            render={() => <Identification back={onBackButtonClick} />}
          />
          <Route
            path={`/:locale?${BOOKING_INFORMATION_PATH}`}
            render={() => <ContactDetails back={onBackButtonClick} />}
          />
          <Route
            path={`/:locale?${BOOKING_PARKING_PATH}`}
            render={() => <ParkingSlots back={onBackButtonClick} />}
          />
          <Route
            path={`/:locale?${BOOKING_SUMMARY_PATH}`}
            render={() => <BookingSummary back={onBackButtonClick} />}
          />
          <Route
            path={`/:locale?${BOOKING_PAYMENT_PROCESSING_PATH}`}
            render={() => <BookingPaymentProcessing />}
          />
        </Switch>
      </div>
    </div>
  );
}
